import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import { ShoppingBag, Favorite, Store } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePortal } from './PortalContext';
import api from '../services/api';

const PortalDashboard = () => {
  const { customer } = usePortal();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [upcomingSales, setUpcomingSales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('portal_access_token');

      const [purchasesRes, salesRes] = await Promise.all([
        api.get('/portal/purchases?per_page=5', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get('/portal/sales?per_page=6')
      ]);

      setPurchases(purchasesRes.data.purchases || []);
      setUpcomingSales(salesRes.data.sales || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Welcome back, {customer?.name}!
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <ShoppingBag sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ mt: 1 }}>My Purchases</Typography>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate('/portal/purchases')}>
              View All
            </Button>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Favorite sx={{ fontSize: 48, color: 'error.main' }} />
            <Typography variant="h6" sx={{ mt: 1 }}>Wishlist</Typography>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate('/portal/wishlist')}>
              View Items
            </Button>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Store sx={{ fontSize: 48, color: 'success.main' }} />
            <Typography variant="h6" sx={{ mt: 1 }}>Browse Sales</Typography>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate('/portal/sales')}>
              Explore
            </Button>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Recent Purchases
      </Typography>
      <Grid container spacing={2}>
        {purchases.length > 0 ? (
          purchases.map((purchase) => (
            <Grid item xs={12} sm={6} md={4} key={purchase.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6">{purchase.item?.name}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    ${purchase.total_amount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(purchase.purchased_at).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Typography color="text.secondary">No purchases yet</Typography>
          </Grid>
        )}
      </Grid>

      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Upcoming Sales
      </Typography>
      <Grid container spacing={2}>
        {upcomingSales.map((sale) => (
          <Grid item xs={12} sm={6} md={4} key={sale.id}>
            <Card sx={{ cursor: 'pointer' }} onClick={() => navigate(`/portal/sales/${sale.id}`)}>
              {sale.primary_photo && (
                <Box
                  component="img"
                  src={`${process.env.REACT_APP_API_URL || 'https://api.paceestate.com'}${sale.primary_photo.file_path}`}
                  alt={sale.name}
                  sx={{ width: '100%', height: 200, objectFit: 'cover' }}
                />
              )}
              <CardContent>
                <Typography variant="h6">{sale.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {sale.city}, {sale.state}
                </Typography>
                <Typography variant="caption">
                  {new Date(sale.start_date).toLocaleDateString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PortalDashboard;
